import type { Permission } from "./permissions";

/**
 * Relationship-Based Permission Rules
 *
 * Permissions are granted per role:relationship pair. If a pair isn't defined,
 * access is denied by default (whitelist model).
 *
 * Relationships for lab roles (based on accession.receivingOrgId):
 *   - at_owning_lab:  receivingOrgId === user.org
 *   - at_other_lab:   any other lab
 *
 * Relationships for org roles (based on accession.orgId):
 *   - at_ordering_org: orgId === user.org
 *   - at_allowed_org:  orgId is in user.allowedOrgs (or via accession_orgs m2m)
 *
 * Admin/manager:
 *   - at_any: unrestricted
 */

const commonRead: Permission[] = [
  "read:accessions",
  "read:accession_comments",
  "read:accession_uploads",
  "read:accession_views",
  "read:holds",
  "read:materials",
  "read:reports",
  "read:results",
  "read:samples",
  "read:subjects",
  "read:test_orders",
];

const commonWrite: Permission[] = [
  "create:accession_comments",
  "create:feedback",
];

const labRead: Permission[] = [
  "read:test_runs",
  "read:audit_logs",
  "read:accession_notes",
  "read:notifications",
  "read:instrument_results",
  "read:printers",
];

export const PERMISSION_RULES: Record<string, Permission[]> = {
  // ── Lab Manager ──────────────────────────────────────────────────────
  "blis_lab_manager:at_any": ["*" as Permission],

  // ── Lab Admin (read everything, write only admin functions) ──────────
  "blis_lab_admin:at_any": [
    ...commonRead,
    ...commonWrite,
    ...labRead,
    "read:invoices",
    "update:invoices",
    "admin:all",
  ],

  // ── Lab Tech ─────────────────────────────────────────────────────────
  "blis_lab_tech:at_owning_lab": [
    ...commonRead,
    ...commonWrite,
    ...labRead,

    "create:accessions",
    "create:subjects",
    "create:accession_comments",
    "create:accession_notes",
    "create:accession_uploads",
    "create:accession_views",
    "create:holds",
    "create:samples",
    "create:test_orders",
    "create:test_runs",
    "create:materials",
    "create:results",
    "create:reports",
    "create:instrument_results",

    "update:accessions",
    "update:accession_uploads",
    "update:accession_views",
    "update:holds",
    "update:materials",
    "update:subjects",
    "update:samples",
    "update:test_orders",
    "update:test_runs",
    "update:results",
    "update:instrument_results",

    "validate:results",

    "delete:accession_uploads",
    "delete:accession_views",
    "delete:samples",
    "delete:test_orders",
    "delete:test_runs",
  ],

  "blis_lab_tech:at_other_lab": [
    ...commonRead,
    ...commonWrite,
    ...labRead,
    "create:results",
  ],

  // ── Lab Assistant ────────────────────────────────────────────────────
  "blis_lab_assistant:at_owning_lab": [
    ...commonRead,
    ...commonWrite,
    ...labRead,

    "create:accessions",
    "create:subjects",
    "create:accession_comments",
    "create:accession_notes",
    "create:accession_uploads",
    "create:accession_views",
    "create:holds",
    "create:samples",
    "create:test_orders",
    "create:test_runs",
    "create:materials",

    "update:accessions",
    "update:accession_uploads",
    "update:accession_views",
    "update:holds",
    "update:materials",
    "update:subjects",
    "update:samples",
    "update:test_orders",
    "update:test_runs",

    "delete:accession_uploads",
    "delete:accession_views",
    "delete:samples",
    "delete:test_orders",
    "delete:test_runs",
  ],

  "blis_lab_assistant:at_other_lab": [],

  // ── Lab Readonly ─────────────────────────────────────────────────────
  "blis_lab_readonly:at_owning_lab": [
    ...commonRead,
    ...commonWrite,
    ...labRead,
  ],

  "blis_lab_readonly:at_other_lab": [],

  // ── Org Tech ─────────────────────────────────────────────────────────
  "blis_org_tech:at_ordering_org": [
    ...commonRead,
    ...commonWrite,
    "create:accessions",
    "create:subjects",
    "create:samples",
    "create:test_orders",
  ],

  "blis_org_tech:at_allowed_org": [...commonRead, ...commonWrite],

  // ── Org Readonly ─────────────────────────────────────────────────────
  "blis_org_readonly:at_ordering_org": [...commonRead, ...commonWrite],

  "blis_org_readonly:at_allowed_org": [...commonRead, ...commonWrite],
};

/**
 * Get the permission list for a given role:relationship key.
 * Returns undefined if the key is not defined (no access).
 */
export function getPermissionsForRule(key: string): Permission[] | undefined {
  return PERMISSION_RULES[key];
}
